import type { AppointmentSettings } from '../@models/appointment-settings';

export const DEFAULT_APPOINTMENT_SETTINGS: Omit<
  AppointmentSettings,
  'shopId' | 'updatedAt' | 'updatedBy'
> = {
  version: 1,
  booking: {
    enabled: true,
    onlineBookingEnabled: true,
    sameDayBookingEnabled: true,
    walkInsEnabled: true,
    guestBookingEnabled: true,
    minimumLeadTimeMinutes: 60,
    maximumAdvanceBookingDays: 60,
    slotIntervalMinutes: 15,
    bookingIncrementMinutes: 15,
  },
  cancellation: {
    allowed: true,
    minimumNoticeHours: 24,
    allowLateCancellation: false,
    lateCancellationFeeEnabled: false,
  },
  rescheduling: { allowed: true, minimumNoticeHours: 12, maximumReschedules: 3 },
  timing: {
    defaultBufferBeforeMinutes: 0,
    defaultBufferAfterMinutes: 10,
    lateArrivalGraceMinutes: 10,
    noShowAfterMinutes: 15,
  },
  confirmation: { mode: 'auto_confirm', requireClientConfirmation: false },
  staffAssignment: {
    allowClientSelectMember: true,
    allowAnyAvailableMember: true,
    autoAssignEnabled: false,
  },
  conflicts: { allowDoubleBooking: false, allowOverbooking: false },
  reminders: { enabled: true, emailEnabled: true, reminderOffsetsMinutes: [1440] },
};

export function normalizeAppointmentSettings(
  shopId: string,
  value?: Partial<AppointmentSettings>,
): AppointmentSettings {
  const settings = value ?? {};
  const booking = { ...DEFAULT_APPOINTMENT_SETTINGS.booking, ...settings.booking };
  if (!booking.enabled) booking.onlineBookingEnabled = false;
  const legacyConfirmation = (settings.confirmation ?? {}) as Partial<{
    autoConfirm: boolean;
    requireShopApproval: boolean;
  }>;
  const requestedMode = (
    settings.confirmation as Partial<AppointmentSettings['confirmation']> | undefined
  )?.mode;
  const {
    autoConfirm: _autoConfirm,
    requireShopApproval: _requireShopApproval,
    ...confirmationOverrides
  } = (settings.confirmation ?? {}) as Record<string, unknown>;
  const confirmation = {
    ...DEFAULT_APPOINTMENT_SETTINGS.confirmation,
    ...confirmationOverrides,
    mode:
      requestedMode ??
      (legacyConfirmation.requireShopApproval || legacyConfirmation.autoConfirm === false
        ? 'shop_approval'
        : 'auto_confirm'),
  };
  return {
    ...DEFAULT_APPOINTMENT_SETTINGS,
    ...settings,
    shopId,
    booking,
    confirmation,
    cancellation: { ...DEFAULT_APPOINTMENT_SETTINGS.cancellation, ...settings.cancellation },
    rescheduling: { ...DEFAULT_APPOINTMENT_SETTINGS.rescheduling, ...settings.rescheduling },
    timing: { ...DEFAULT_APPOINTMENT_SETTINGS.timing, ...settings.timing },
    staffAssignment: {
      ...DEFAULT_APPOINTMENT_SETTINGS.staffAssignment,
      ...settings.staffAssignment,
    },
    conflicts: { ...DEFAULT_APPOINTMENT_SETTINGS.conflicts, ...settings.conflicts },
    reminders: { ...DEFAULT_APPOINTMENT_SETTINGS.reminders, ...settings.reminders },
  };
}
